import { useState, useCallback, useMemo } from 'react';
import { getDeleteModalOptions } from './getDeleteModalOptions';
import { getDeleteInProgressModalOptions } from './getDeleteInProgressModalOptions';
import { getKeepFilesModalOptions } from './useKeepFilesInUse/getKeepFilesInUse';
import { FileAwarenessModalStep, USED_FILES_PAGE_SIZE } from '../constants';
import { DeleteMode } from '@wix/ambassador-wix-privatemedia-proxy-server/types';
import { mediaManagerTrashActions, filesInUseActionClick } from '@wix/bi-logger-media-data/v2';
import { useFakeProgress } from '../../../common/hooks/useFakeProgress';
import { DAYS_IN_USE_TO_CHECK } from '../constants/modalConstants';
export const useFileAwarenessModalOptions = _ref => {
  var _modalState$filesInUs5, _modalState$filesInUs6, _modalState$filesInUs7;
  let {
    folderIds,
    fileIds,
    pageName,
    tabName,
    clickOrigin,
    mediaManagerService,
    biLogger,
    errorLogger,
    formatMessage,
    formatDate,
    closeModal,
    onDeleteError,
    onFilesDeleted
  } = _ref;
  const [modalState, setModalState] = useState({
    step: FileAwarenessModalStep.DELETE_MODAL,
    isConfirmChecked: false
  });
  const {
    progress: fakeProgress,
    finishProgress: finishFakeProgress,
    startProgress: startFakeProgress
  } = useFakeProgress({
    initialProgress: 0.25,
    interval: 200,
    edgeTimeout: 1000
  });
  const allRequestedIds = fileIds.concat(folderIds);
  const selectedItemsCount = allRequestedIds.length;
  const isClearTrash = selectedItemsCount === 0;
  const commonBiFields = useMemo(() => ({
    batchSize: allRequestedIds.length,
    pageName,
    fileId: allRequestedIds.toString(),
    clickOrigin,
    tabName
  }), [allRequestedIds, pageName, clickOrigin, tabName]);
  const handleResetFileAwarenessState = useCallback(() => {
    setModalState({
      step: FileAwarenessModalStep.DELETE_MODAL,
      isConfirmChecked: false
    });
  }, []);
  const toggleConfirmCheckbox = useCallback(() => {
    if (modalState.step === FileAwarenessModalStep.DELETE_MODAL) {
      setModalState(() => ({
        step: FileAwarenessModalStep.DELETE_MODAL,
        isConfirmChecked: !modalState.isConfirmChecked
      }));
    }
  }, [modalState]);
  const onFilesDeletedHandler = useCallback(filesDeletedInfo => {
    biLogger.report(mediaManagerTrashActions({
      ...commonBiFields,
      target: filesDeletedInfo.isUsedFiles ? 'delete_forever_used' : 'delete_forever_unused',
      batchSize: filesDeletedInfo.deletedFilesCount,
      batch: filesDeletedInfo.deletedFilesCount !== 1,
      // zero is also batch
      fileId: Array.from(filesDeletedInfo.deletedItemIds).toString()
    }));
    onFilesDeleted(filesDeletedInfo);
  }, [commonBiFields, biLogger, onFilesDeleted]);
  const loadMoreUsedFiles = useCallback(async () => {
    var _modalState$filesInUs;
    if (modalState.step !== FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL || !((_modalState$filesInUs = modalState.filesInUse.pagingMetadata) != null && _modalState$filesInUs.hasNext) || modalState.isLoadError) {
      return;
    }
    const {
      filesInUse
    } = modalState;
    try {
      var _modalState$filesInUs2, _filesInUse$files;
      const nextPage = await mediaManagerService.listTrashBinFilesInUse({
        fileIds,
        folderIds,
        usedInLastDays: DAYS_IN_USE_TO_CHECK,
        paging: {
          cursor: (_modalState$filesInUs2 = modalState.filesInUse.pagingMetadata) == null || (_modalState$filesInUs2 = _modalState$filesInUs2.cursors) == null ? void 0 : _modalState$filesInUs2.next,
          limit: USED_FILES_PAGE_SIZE
        },
        allContent: isClearTrash
      });
      setModalState({
        step: FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL,
        isLoadError: false,
        filesInUse: {
          operationId: filesInUse.operationId,
          files: (_filesInUse$files = filesInUse.files) == null ? void 0 : _filesInUse$files.concat(nextPage.files ?? []),
          pagingMetadata: nextPage.pagingMetadata
        }
      });
    } catch (error) {
      errorLogger(error);
      setModalState({
        step: FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL,
        filesInUse,
        isLoadError: true
      });
    }
  }, [errorLogger, fileIds, folderIds, isClearTrash, mediaManagerService, modalState]);
  const deleteUsedFiles = useCallback(async () => {
    return mediaManagerService.bulkDelete({
      fileIds,
      folderIds,
      options: {
        allTrashContent: isClearTrash,
        permanent: true,
        deleteMode: DeleteMode.ALL
      }
    });
  }, [fileIds, folderIds, isClearTrash, mediaManagerService]);
  const handleSubmitInitialDeleteFiles = useCallback(async () => {
    setModalState({
      step: FileAwarenessModalStep.DELETE_IN_PROGRESS_MODAL,
      totalUsed: 0
    });
    startFakeProgress();
    mediaManagerService.bulkDelete({
      fileIds,
      folderIds,
      pagingLimit: USED_FILES_PAGE_SIZE,
      options: {
        usedInLastDays: DAYS_IN_USE_TO_CHECK,
        allTrashContent: isClearTrash,
        permanent: true,
        deleteMode: DeleteMode.ONLY_UNUSED
      }
    }).then(async deleteResult => {
      var _deleteResult$pagingM;
      const hasIndirectFilesSelected = fileIds.length === 0 && folderIds.length === 0 || folderIds.length > 0;
      const totalUsed = deleteResult == null || (_deleteResult$pagingM = deleteResult.pagingMetadata) == null ? void 0 : _deleteResult$pagingM.total;
      if (totalUsed) {
        var _deleteResult$files;
        setModalState({
          step: FileAwarenessModalStep.DELETE_IN_PROGRESS_MODAL,
          totalUsed
        });
        const usedFiles = new Set((_deleteResult$files = deleteResult.files) == null ? void 0 : _deleteResult$files.map(file => file.id));
        const unusedRequestedFiles = fileIds.reduce((unused, file) => usedFiles.has(file) ? unused : unused.add(file), new Set());
        if (unusedRequestedFiles.size > 0 || hasIndirectFilesSelected) {
          onFilesDeletedHandler({
            operationId: deleteResult.operationId,
            deletedFilesCount: unusedRequestedFiles.size,
            deletedItemIds: unusedRequestedFiles,
            isUsedFiles: false,
            hasIndirectFilesSelected
          });
        }
      } else {
        onFilesDeletedHandler({
          operationId: deleteResult == null ? void 0 : deleteResult.operationId,
          deletedFilesCount: allRequestedIds.length,
          deletedItemIds: new Set(allRequestedIds),
          isUsedFiles: false,
          hasIndirectFilesSelected
        });
      }
      finishFakeProgress(async () => {
        if (totalUsed) {
          setModalState({
            step: FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL,
            filesInUse: deleteResult,
            isLoadError: false
          });
        } else {
          closeModal();
        }
      });
    }).catch(error => {
      errorLogger(error);
      onDeleteError(error);
      closeModal();
    });
  }, [fileIds, folderIds, startFakeProgress, mediaManagerService, isClearTrash, finishFakeProgress, onFilesDeletedHandler, closeModal, allRequestedIds, errorLogger, onDeleteError]);
  const handleSubmitDeleteUsedFiles = useCallback(async () => {
    if (modalState.step !== FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL) {
      return;
    }
    try {
      var _filesInUse$pagingMet;
      const filesInUse = modalState.filesInUse;
      const totalUsedFiles = ((_filesInUse$pagingMet = filesInUse.pagingMetadata) == null ? void 0 : _filesInUse$pagingMet.total) ?? 0;
      biLogger.report(filesInUseActionClick({
        ...commonBiFields,
        action: 'delete',
        filesInUse: totalUsedFiles
      }));
      closeModal();
      const deleteResult = await deleteUsedFiles();
      onFilesDeletedHandler({
        operationId: deleteResult.operationId,
        deletedFilesCount: totalUsedFiles,
        deletedItemIds: new Set(filesInUse.files.map(file => file.id).concat(folderIds)),
        isUsedFiles: true
      });
    } catch (error) {
      onDeleteError(error);
    }
  }, [biLogger, folderIds, modalState, commonBiFields, closeModal, deleteUsedFiles, onFilesDeletedHandler, onDeleteError]);
  const handleKeepFiles = useCallback(() => {
    var _modalState$filesInUs3;
    if (modalState.step !== FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL) {
      return;
    }
    biLogger.report(filesInUseActionClick({
      ...commonBiFields,
      action: 'keep',
      filesInUse: (_modalState$filesInUs3 = modalState.filesInUse) == null || (_modalState$filesInUs3 = _modalState$filesInUs3.pagingMetadata) == null ? void 0 : _modalState$filesInUs3.total
    }));
    closeModal();
  }, [biLogger, closeModal, commonBiFields, modalState]);
  const handleCloseWithCloseButton = useCallback(() => {
    var _modalState$filesInUs4;
    if (modalState.step !== FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL) {
      return;
    }
    biLogger.report(filesInUseActionClick({
      ...commonBiFields,
      action: 'x',
      filesInUse: (_modalState$filesInUs4 = modalState.filesInUse) == null || (_modalState$filesInUs4 = _modalState$filesInUs4.pagingMetadata) == null ? void 0 : _modalState$filesInUs4.total
    }));
  }, [biLogger, commonBiFields, modalState]);
  const handleCloseKeepFileModal = useCallback(() => {
    closeModal();
  }, [closeModal]);
  const isSiteFilesEntry = tabName === 'storage_manager_site_files';
  switch (modalState.step) {
    case FileAwarenessModalStep.DELETE_MODAL:
      return getDeleteModalOptions({
        isSiteFilesEntry,
        isConfirmChecked: modalState.isConfirmChecked,
        selectedItemsCount,
        closeModal,
        formatMessage,
        toggleConfirmCheckbox,
        deleteFiles: handleSubmitInitialDeleteFiles,
        onAfterClose: handleResetFileAwarenessState
      });
    case FileAwarenessModalStep.DELETE_IN_PROGRESS_MODAL:
      return getDeleteInProgressModalOptions({
        isSiteFilesEntry,
        progress: fakeProgress,
        usedFilesCount: modalState.totalUsed ?? 0,
        selectedItemsCount,
        formatMessage,
        onAfterClose: handleResetFileAwarenessState
      });
    case FileAwarenessModalStep.KEEP_FILES_IN_USE_MODAL:
      return getKeepFilesModalOptions({
        usedFilesCount: ((_modalState$filesInUs5 = modalState.filesInUse.pagingMetadata) == null ? void 0 : _modalState$filesInUs5.total) ?? 0,
        usedFiles: ((_modalState$filesInUs6 = modalState.filesInUse) == null ? void 0 : _modalState$filesInUs6.files) ?? [],
        hasMore: ((_modalState$filesInUs7 = modalState.filesInUse) == null || (_modalState$filesInUs7 = _modalState$filesInUs7.pagingMetadata) == null ? void 0 : _modalState$filesInUs7.hasNext) ?? false,
        isLoadError: modalState.isLoadError,
        loadMoreFiles: loadMoreUsedFiles,
        deleteFiles: handleSubmitDeleteUsedFiles,
        closeModal: handleCloseKeepFileModal,
        onCloseWithCloseButtonClick: handleCloseWithCloseButton,
        keepFiles: handleKeepFiles,
        onAfterClose: handleResetFileAwarenessState,
        formatMessage,
        formatDate
      });
  }
};
//# sourceMappingURL=useFileAwarenessModalOptions.js.map